import React from "react";
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { SITE, getContact } from "@/lib/site";
import { useContent } from "@/lib/content";

export default function Footer() {
  const { content } = useContent();
  const c = getContact(content);
  const f = content.footer || {};
  const s = content.social || {};
  const year = new Date().getFullYear();

  const socials = [
    { url: s.facebook, Icon: Facebook, label: "Facebook", testid: "social-facebook" },
    { url: s.instagram, Icon: Instagram, label: "Instagram", testid: "social-instagram" },
    { url: s.linkedin, Icon: Linkedin, label: "LinkedIn", testid: "social-linkedin" },
    { url: s.youtube, Icon: Youtube, label: "YouTube", testid: "social-youtube" },
  ].filter((x) => x.url && x.url.trim());
  return (
    <footer data-testid="site-footer" className="bg-[#001a52] text-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <img src={SITE.logo.white} alt={SITE.name} className="h-14 w-auto object-contain bg-white rounded-xl p-2 inline-block" />
            <p className="mt-6 font-inter text-sm text-white/75 leading-relaxed max-w-md">
              {f.tagline || "Educators Institute of Management — From admission to placement, we stay with you. Career programs in ACCA, CMA USA, CA, CS and CFA with 100% support till you pass and get placed."}
            </p>
            <p className="mt-6 font-rubik text-base font-semibold text-[#73c3ff]">"{c.positioning}"</p>
          </div>

          <div className="md:col-span-3">
            <p className="font-rubik text-sm font-semibold uppercase tracking-wider text-white/60">Explore</p>
            <ul className="mt-4 space-y-3 font-inter text-sm">
              <li><a href="/" className="text-white/80 hover:text-[#73c3ff]">Home</a></li>
              <li><a href="/faculty" className="text-white/80 hover:text-[#73c3ff]">Faculty</a></li>
              <li><a href="/toppers" className="text-white/80 hover:text-[#73c3ff]">Toppers & Pass-Outs</a></li>
              <li><a href="/courses/acca" className="text-white/80 hover:text-[#73c3ff]">ACCA</a></li>
              <li><a href="/courses/cma-usa" className="text-white/80 hover:text-[#73c3ff]">CMA USA</a></li>
              <li><a href="/courses/ca" className="text-white/80 hover:text-[#73c3ff]">CA</a></li>
              <li><a href="/courses/cs" className="text-white/80 hover:text-[#73c3ff]">CS</a></li>
              <li><a href="/courses/cfa" className="text-white/80 hover:text-[#73c3ff]">CFA</a></li>
              <li><a href="/#fees" className="text-white/80 hover:text-[#73c3ff]">Fees & EMI</a></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-rubik text-sm font-semibold uppercase tracking-wider text-white/60">Reach Us</p>
            <ul className="mt-4 space-y-4 font-inter text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-[#73c3ff]" />
                <a href={`tel:${c.phoneRaw}`} className="text-white/85 hover:text-[#73c3ff]" data-testid="footer-phone">{c.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-[#73c3ff]" />
                <a href={`mailto:${c.email}`} className="text-white/85 hover:text-[#73c3ff] break-all" data-testid="footer-email">{c.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#73c3ff]" />
                <span className="text-white/85 leading-relaxed">{c.address}</span>
              </li>
            </ul>
            <div className="mt-6 flex items-center gap-3">
              {socials.length === 0 ? (
                <p className="font-inter text-xs text-white/40">No social links configured</p>
              ) : socials.map(({ url, Icon, label, testid }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={testid}
                  aria-label={label}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#73c3ff] hover:text-[#002570] flex items-center justify-center transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <p className="font-inter text-xs text-white/60">© {year} {SITE.name}. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <a href="/student/login" data-testid="footer-student-login-link" className="font-inter text-xs text-white/60 hover:text-[#73c3ff] transition-colors">Student Login</a>
            <a href="/admin/login" data-testid="footer-admin-login-link" className="font-inter text-xs text-white/60 hover:text-[#73c3ff] transition-colors">Admin Login</a>
            <p className="font-inter text-xs text-white/60">{f.credit || "Built for students who refuse to settle."}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
